import React from 'react';
import { Chip } from '@mui/material';
import { Text, Theme, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { helpers } from '@simplybenefits/sb-utils';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { 
    Add as AddIcon, 
    Remove as RemoveIcon 
} from '@mui/icons-material';

/**
 *
 * @param {*} props
 */
export default function Drugs ({ classes, expanded, drugs, handleChange, defineDeductibleVerbiage, showDependentWarning, waivedEHC, isStudent }) {
    const appendSubstitute = ![ 'Generic Substitution', 'Simply Protected Formulary', 'Simply Protected TC Formulary' ].includes(drugs.genericSubstitute);

    const genericSubstitute = appendSubstitute
        ? `${ drugs.genericSubstitute } Substitute`
        : drugs.genericSubstitute;

    return (
        <div style={ classes.block }>
            <Accordion
                square
                onChange={handleChange('drugs')}
                style={{ boxShadow: 'none' }}
                disableGutters={true}
            >
                <AccordionSummary
                    classes={{
                        content: classes.panelContent,
                        expanded: classes.expanded,
                        root: classes.root
                    }}
                    expandIcon={expanded.includes('drugs') ? <RemoveIcon /> : <AddIcon />}
                    sx={classes.accordionSummary}
                >
                    <Text medium bold>Drugs</Text>
                    {
                        waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "0 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                    }
                </AccordionSummary>
                <AccordionDetails style={classes.panelDetails}>
                    {
                        drugs.isEligible !== 1 ?
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    You are currently ineligible for this benefit, if this is a mistake please contact your administrator.
                                </Text>
                            </div>
                            : null
                    }

                    {
                        showDependentWarning &&
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    You have waived this benefit for your dependents.
                                </Text>
                            </div>
                    }

                    {
                        drugs.isGraded ?
                            <Text small bold margin="5px 0px 10px 0px">{ `Graded scale as follows: ${ drugs.firstPercentage }% of the first $${ helpers.formatNumber(drugs.firstMax) } spent per year, ${ drugs.secondPercentage }% of ${ (drugs.secondMax > 0 && drugs.secondMax !== 'Unlimited') ? `next $${ helpers.formatNumber(drugs.secondMax) } spent` : `the remainder spent` } per year.` }</Text>
                            :
                            <Text small bold margin="5px 0px 10px 0px">{ `${ drugs.reimbursement }% Reimbursement` }</Text>
                    }

                    {
                        drugs.familyCombinedMaximum && drugs.familyCombinedMaximum !== 0 ?
                            <div style={classes.row}>
                                <Text small bold margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.combinedFamilyMaximum, { combinedMaximum: helpers.formatNumber(drugs.familyCombinedMaximum) }) }
                                </Text>
                            </div>
                            : null
                    }

                    {
                        drugs.isPreferredProvider ?
                            <>
                                <Text small bold margin="5px 0px 0px 0px">{ `${ drugs.preferredProviderReimbursement }% Preferred Provider Reimbursement` }</Text>
                                <Text small bold margin="5px 0px 0px 0px">{ `Preferred Provider: ${ drugs.preferredProvider }` }</Text>
                            </>
                            : null
                    }

                    <div>
                        {
                            (drugs.hasDeductible && !drugs.deductibleEqualToDispenseFee) ?
                                defineDeductibleVerbiage(drugs, false)
                                : null
                        }

                        {
                            drugs.hasOverallMax ?
                                <Text small margin="5px 0px 0px 0px">{ `$${ helpers.formatNumber(drugs.overallMax) } Overall Maximum` }</Text>
                                : null
                        }

                        <Text small margin="5px 0px 0px 0px">{ genericSubstitute }</Text>

                        {
                            (drugs.hasDeductible && drugs.deductibleEqualToDispenseFee) ?
                                <Text small margin="5px 0px 0px 0px">Deductible equal to Dispense Fee</Text>
                                : null
                        }

                        {
                            drugs.hasDispenseFeeMax ?
                                <Text small margin="5px 0px 0px 0px">{ `$${ helpers.formatNumber(drugs.dispenseFeeMax) } Dispense Fee Maximum` }</Text>
                                : null
                        }

                        {
                            drugs.includeErectDysfuncDrugs ?
                                <>
                                    <Text small margin="5px 0px 0px 0px">{ `$${ helpers.formatNumber(drugs.edMax) } Erectile Dysfunction Maximum per ${ drugs.edUsagePeriod.toLocaleLowerCase() }` }</Text>
                                    <Text small margin="5px 0px 0px 0px">{ `${ drugs.edReimbursement }% Erectile Dysfunction Reimbursement` }</Text>
                                </>
                                : null
                        }

                        {
                            drugs.includeFertilityDrugs ?
                                <>
                                    <Text small margin="5px 0px 0px 0px">{ `$${ helpers.formatNumber(drugs.fertilityMax) } Fertility Drugs Maximum` }</Text>
                                    <Text small margin="5px 0px 0px 0px">{ `${ drugs.fertilityReimbursement }% Fertility Drugs Reimbursement` }</Text>
                                </>
                                : null
                        }

                        {
                            drugs.includePreventativeVaccines ?
                                <>
                                    {
                                        drugs.preventativeVaccinesIsRC ?
                                            <>
                                                <Text small margin="5px 0px 0px 0px">{ `Reasonable and customary expenses for Preventative Vaccines` }</Text>
                                                <Text small margin="5px 0px 0px 0px">{ `${ drugs.preventativeVaccinesReimbursement }% Preventative Vaccines Reimbursement` }</Text>
                                            </>
                                            :
                                            <>
                                                <Text small margin="5px 0px 0px 0px">{ `$${ helpers.formatNumber(drugs.preventativeVaccinesMax) } Preventative Vaccines Maximum` }</Text>

                                                <Text small margin="5px 0px 0px 0px">{ `${ drugs.preventativeVaccinesReimbursement }% Preventative Vaccines Reimbursement` }</Text>
                                            </>
                                    }
                                </>
                                : null
                        }

                        {
                            drugs.includeSmokingCessations ?
                                <>
                                    <Text small margin="5px 0px 0px 0px">{ `$${ helpers.formatNumber(drugs.smokingCessationsMax) } Smoking Cessation Maximum` }</Text>
                                    <Text small margin="5px 0px 0px 0px">{ `${ drugs.smokingCessationsReimbursement }% Smoking Cessation Reimbursement` }</Text>
                                </>
                                : null
                        }

                        {
                            isStudent ?
                                <Text small margin="5px 0px 0px 0px">{ `Terminates at age ${ drugs.terminationAge } or date of participants study if earlier.` }</Text>
                                :
                                <Text small margin="5px 0px 0px 0px">{ `Terminates at age ${ drugs.terminationAge } or date of participants retirement if earlier.` }</Text>
                        }
                    </div>

                    <div style={{ marginTop: 10, marginBottom: 10 }}>
                        <Text small bold margin="5px 0px 0px 0px">
                            {`Some drugs may have alternative coverage or maximums, please consult the member booklet for more information.`}
                        </Text>
                    </div>

                </AccordionDetails>
            </Accordion>
        </div>
    );
}